import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { LayoutDashboard, Users, Bot, CreditCard, Settings, Shield, ChevronLeft, ChevronRight, LogOut, BarChart3 } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/admin', label: 'Overview', icon: LayoutDashboard, exact: true },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/agents', label: 'Agents', icon: Bot },
  { path: '/admin/payments', label: 'Payments', icon: CreditCard },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

export default function AdminLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  return (
    <div className="min-h-screen bg-[#020202] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-16' : 'w-64'} bg-[#0a0a0a] border-r border-white/5 flex flex-col transition-all duration-300 fixed h-full z-40`} data-testid="admin-sidebar">
        <div className="p-4 border-b border-white/5 flex items-center justify-between">
          {!collapsed && (
            <Link to="/admin" className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#FF7A3D]" />
              <span className="font-heading font-bold text-sm text-white">ADMIN<span className="text-[#ff003c]">CTRL</span></span>
            </Link>
          )}
          {collapsed && <Shield className="w-5 h-5 text-[#FF7A3D] mx-auto" />}
          <button onClick={() => setCollapsed(!collapsed)} className="text-[#aaa] hover:text-white transition-colors" data-testid="sidebar-toggle">
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        <nav className="flex-1 p-2 space-y-1">
          {NAV_ITEMS.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all ${
                isActive(item)
                  ? 'bg-[#FF7A3D]/10 text-[#FF7A3D] border border-[#FF7A3D]/20'
                  : 'text-[#999] hover:text-white hover:bg-white/[0.03] border border-transparent'
              }`}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span className="font-mono text-xs tracking-wide">{item.label.toUpperCase()}</span>}
            </Link>
          ))}
        </nav>

        <div className="p-2 border-t border-white/5 space-y-1">
          <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-[#999] hover:text-[#00E5C1] hover:bg-white/[0.03] transition-all" data-testid="admin-to-platform">
            <BarChart3 className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span className="font-mono text-xs">PLATFORM</span>}
          </Link>
          <button onClick={() => { logout(); navigate('/'); }} className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-[#999] hover:text-[#ff003c] hover:bg-white/[0.03] transition-all w-full" data-testid="admin-logout">
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span className="font-mono text-xs">EXIT</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${collapsed ? 'ml-16' : 'ml-64'} transition-all duration-300`}>
        <div className="p-6 md:p-10">
          {children}
        </div>
      </main>
    </div>
  );
}
